import { useMemo } from 'react';
import type { CardVariant } from './WhitePaperCard';
import type { Study } from 'content/science/studyTypes';

const TONES: CardVariant[] = ['white', 'grey', 'dark'];

/* Cards the grid shows before "View more". */
const PAGE = 6;
/* A clash on that opening page counts for this much more than one behind the
   fold, so the page everyone actually sees is the one that comes out clean. */
const PAGE_WEIGHT = 8;
/* The next card along touches at three columns and again at one, so it is
   worth twice the card three later, which only touches at three. */
const NEXT_WEIGHT = 2;
/* Pull toward an even mix of tones on each opening page. Deliberately well
   below a clash: separating two cards matters more than the exact mix. */
const BALANCE = 4;
/* Repair passes. Converges in two or three; the cap just bounds the work. */
const SWEEPS = 4;

/**
 * A tone per study, fixed to the study rather than to the cell it lands in,
 * so filtering re-orders the grid without repainting it.
 *
 * Because the tone can't react to whoever ends up beside it, the neighbours
 * are worked out in advance. Every pair of cards that would touch in the
 * unfiltered grid, or under any one category filter, becomes a conflict; the
 * studies are then coloured against that, each taking the tone its
 * neighbours use least, and the result is swept a few times to move any card
 * still clashing. Pairs on an opening page are weighted heavily, so the
 * default view of every filter is what the solver protects first.
 *
 * Colouring runs oldest first. A newly published study goes to the front of
 * every list it belongs to, which leaves the existing cards with the same
 * neighbours they already had, so most of them keep the tone they had too.
 * Nothing is configured per study.
 *
 * Two columns is the one width left out. Keeping the next card, the one two
 * later and the one three later all distinct means every run of four cards
 * is four different colours, which three tones cannot do — so one column
 * count has to give, and the tablet range is the least costly to lose.
 *
 * Pass the unfiltered list: the conflicts have to come from every filter
 * state at once, not just the one on screen.
 */
export const useStudyTones = (studies: Study[]): Map<string, CardVariant> =>
  useMemo(() => {
    /* The unfiltered grid, then the grid under each category filter, each in
       the order the section renders it. */
    const views: Study[][] = [studies];
    const categories = new Set<string>();
    studies.forEach((s) => {
      if (categories.has(s.category)) return;
      categories.add(s.category);
      views.push(studies.filter((x) => x.category === s.category));
    });

    const conflicts = new Map<string, Map<string, number>>();
    const link = (a: string, b: string, weight: number) => {
      const edges = conflicts.get(a) ?? new Map<string, number>();
      edges.set(b, (edges.get(b) ?? 0) + weight);
      conflicts.set(a, edges);
    };

    /* Which opening pages a study appears on, for the balance term. */
    const openingPages = new Map<string, number[]>();

    views.forEach((list, view) => {
      list.forEach((study, i) => {
        const onPage = i < PAGE;
        const weight = onPage ? PAGE_WEIGHT : 1;
        if (onPage) {
          openingPages.set(study.slug, [
            ...(openingPages.get(study.slug) ?? []),
            view,
          ]);
        }
        [
          [1, weight * NEXT_WEIGHT],
          [3, weight],
        ].forEach(([offset, w]) => {
          const other = list[i + offset];
          if (!other) return;
          link(study.slug, other.slug, w);
          link(other.slug, study.slug, w);
        });
      });
    });

    const tones = new Map<string, CardVariant>();
    const usage = TONES.map(() => 0);
    const pageUsage = views.map(() => TONES.map(() => 0));

    /* Cost of giving `slug` each tone. `held` is the tone it currently has,
       discounted so a card is not counted as its own neighbour on a resweep. */
    const costs = (slug: string, held: number) => {
      const edges = conflicts.get(slug);
      const pages = openingPages.get(slug);
      return TONES.map((tone, i) => {
        let cost = 0;
        edges?.forEach((weight, other) => {
          if (tones.get(other) === tone) cost += weight;
        });
        pages?.forEach((view) => {
          cost += BALANCE * (pageUsage[view][i] - (held === i ? 1 : 0));
        });
        return cost;
      });
    };

    const cheapest = (slug: string, held: number) => {
      const cost = costs(slug, held);
      let pick = 0;
      for (let i = 1; i < TONES.length; i += 1) {
        const better =
          cost[i] < cost[pick] ||
          (cost[i] === cost[pick] &&
            usage[i] - (held === i ? 1 : 0) < usage[pick] - (held === pick ? 1 : 0));
        if (better) pick = i;
      }
      return pick;
    };

    const take = (slug: string, tone: number, delta: number) => {
      usage[tone] += delta;
      openingPages.get(slug)?.forEach((view) => {
        pageUsage[view][tone] += delta;
      });
    };

    /* Oldest first, so a new publication lands last and leaves the tones
       already decided alone. */
    const order = [...studies].reverse();
    order.forEach((study) => {
      const tone = cheapest(study.slug, -1);
      tones.set(study.slug, TONES[tone]);
      take(study.slug, tone, 1);
    });

    /* Move whatever still clashes, cards on an opening page first. */
    const resweep = [
      ...order.filter((s) => openingPages.has(s.slug)),
      ...order.filter((s) => !openingPages.has(s.slug)),
    ];
    for (let sweep = 0; sweep < SWEEPS; sweep += 1) {
      let moved = false;
      resweep.forEach((study) => {
        const held = TONES.indexOf(tones.get(study.slug) as CardVariant);
        const tone = cheapest(study.slug, held);
        if (tone === held) return;
        take(study.slug, held, -1);
        take(study.slug, tone, 1);
        tones.set(study.slug, TONES[tone]);
        moved = true;
      });
      if (!moved) break;
    }

    return tones;
  }, [studies]);

export default useStudyTones;
